/* global HSThemeAppearance */
/* global HSSideNav */
/* global bootstrap */

import React, { useContext, useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import LanguageContext from "../utils/Contexts/LanguageContext";
import ThemeContext from "../utils/Contexts/ThemeContext";
import ContentIcon from "./svg/Content";
import { useCart } from "../utils/Contexts/CartContext";
import Constant from "./Constants";
import _ from "lodash";

function Sidebar() {
  const location = useLocation();
  const { themeStatus } = Constant();
  const { selectedTheme } = useContext(ThemeContext);
  const [selectedIcon, setSelectedIcon] = useState("");
  const { t, changeLanguage, language, languageOptions } =
    useContext(LanguageContext);
  const { setSelectedTheme } = useContext(ThemeContext);
  const selectedLanguage = languageOptions[language] || languageOptions.en;

  let tooltipInit = false;
  useEffect(() => {
    if (!tooltipInit) {
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]'
      );
      [...tooltipTriggerList].forEach(
        (tooltipTriggerEl) => new bootstrap.Tooltip(tooltipTriggerEl)
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
      tooltipInit = true;
    }
  }, []);

  useEffect(() => {
    const $variants = document.querySelectorAll(
      `[aria-labelledby="selectThemeDropdown"] [data-icon]`
    );

    const setActiveStyle = () => {
      $variants.forEach(($item) => {
        const themeValue = $item.getAttribute("data-value");
        const currentAppearance = HSThemeAppearance.getOriginalAppearance();

        if (themeValue === currentAppearance) {
          setSelectedIcon($item.getAttribute("data-icon"));
          setSelectedTheme(themeValue); // Set selected theme name
          $item.classList.add("active");
        } else {
          $item.classList.remove("active");
        }
      });
    };

    $variants.forEach(($item) => {
      $item.addEventListener("click", () => {
        const themeValue = $item.getAttribute("data-value");
        HSThemeAppearance.setAppearance(themeValue);
        setActiveStyle(); // Re-run the style setting after appearance change
      });
    });

    setActiveStyle();

    const onAppearanceChange = () => setActiveStyle();
    window.addEventListener("on-hs-appearance-change", onAppearanceChange);

    return () => {
      window.removeEventListener("on-hs-appearance-change", onAppearanceChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navbarInitRef = useRef(null);
  useEffect(() => {
    if (!navbarInitRef.current) {
      new HSSideNav(".js-navbar-vertical-aside").init();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      navbarInitRef.current = true;
    }
  }, []);

  const { cartCount, fetchCartItems } = useCart();
  const previousCartDataRef = useRef(null);
  useEffect(() => {
    if (!_.isEqual(previousCartDataRef.current, cartCount)) {
      previousCartDataRef.current = cartCount;
      fetchCartItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <aside
      className={`js-navbar-vertical-aside navbar navbar-vertical-aside navbar-vertical navbar-vertical-fixed navbar-expand-xl navbar-bordered bg-white ${themeStatus?.[selectedTheme]}`}
    >
      <div className="navbar-vertical-container">
        <div className="navbar-vertical-footer-offset">
          <div className="navbar-vertical-content content-side">
            <div
              id="navbarVerticalMenu"
              className="nav nav-pills nav-vertical card-navbar-nav pb-0 mb-3"
            >
              <div className="nav-item">
                <Link
                  to="/"
                  className={
                    location.pathname === "/" ? "nav-link active" : "nav-link"
                  }
                >
                  <i className="bi-house-door nav-icon"></i>
                  <span className="nav-link-title">{t("Dashboard")}</span>
                </Link>
              </div>

              <div className="nav-item">
                <a
                  className={`nav-link dropdown-toggle ${
                    [
                      "/content",
                      "/content/keyword-research",
                      "/content/topical-maps",
                      "/content/articles",
                      "/content/editor",
                    ].includes(location.pathname)
                      ? "active-link"
                      : ""
                  }`}
                  href="#navbarVerticalMenuPagesMenu2"
                  role="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#navbarVerticalMenuPagesMenu2"
                  aria-expanded={
                    [
                      "/content",
                      "/content/keyword-research",
                      "/content/topical-maps",
                    ].includes(location.pathname)
                      ? "true"
                      : "false"
                  }
                  aria-controls="navbarVerticalMenuPagesMenu2"
                >
                  <i className="nav-icon">
                    <ContentIcon />
                  </i>
                  <span className="nav-link-title">{t("Content")}</span>
                </a>

                <div
                  id="navbarVerticalMenuPagesMenu2"
                  className={`nav-collapse collapse ${
                    [
                      "/content",
                      "/content/keyword-research",
                      "/content/topical-maps",
                    ]?.some((path) => location.pathname.startsWith(path))
                      ? "show"
                      : ""
                  }`}
                  data-bs-parent="#navbarVerticalMenuPagesMenu2"
                >
                  <Link className="nav-link" to="">
                    {t("Topical Maps")}
                  </Link>
                  <Link className="nav-link" to="">
                    {t("Keyword Research")}
                  </Link>
                  <Link className="nav-link" to="">
                    {t("Content Editor")}
                  </Link>
                  <Link
                    className={
                      location.pathname.startsWith("/content/articles") ||
                      location.pathname.startsWith("/content/editor")
                        ? "nav-link active"
                        : "nav-link"
                    }
                    to="/content/articles"
                  >
                    {t("Articles")}
                  </Link>
                </div>
              </div>

              <div className="nav-item">
                <a
                  className={`nav-link dropdown-toggle ${
                    ["/offers-search", "/monitor"].includes(location.pathname)
                      ? "active-link"
                      : ""
                  }`}
                  href="#navbarVerticalMenuPagesMenu1"
                  role="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#navbarVerticalMenuPagesMenu1"
                  aria-controls="navbarVerticalMenuPagesMenu1"
                  style={{
                    color:
                      (location.pathname === "/offers-search" ||
                        location.pathname === "/monitor") &&
                      "var(--nav-item-title-active) !important",
                  }}
                >
                  <i className="bi-link-45deg nav-icon"></i>
                  <span className="nav-link-title">{t("Backlinks")}</span>
                </a>

                <div
                  id="navbarVerticalMenuPagesMenu1"
                  className={`nav-collapse collapse ${
                    ["/offers-search", "/monitor"].includes(location.pathname)
                      ? "show"
                      : ""
                  }`}
                  data-bs-parent="#navbarVerticalMenuPagesMenu1"
                >
                  <Link
                    to="/offers-search"
                    className={`nav-link ${
                      location.pathname === "/offers-search" ? "active" : ""
                    }`}
                  >
                    {t("Offers Search")}
                  </Link>
                  <Link
                    to="/monitor"
                    className={`nav-link ${
                      location.pathname === "/monitor" ? "active" : ""
                    }`}
                  >
                    {t("Monitor")}
                  </Link>
                </div>
              </div>

              <div className="nav-item">
                <a
                  className={`nav-link dropdown-toggle ${
                    ["/websites", "/offers/import", "/offers/export"].includes(
                      location.pathname
                    )
                      ? "active-link"
                      : ""
                  }`}
                  href="#navbarVerticalMenuPagesMenu4"
                  role="button"
                  style={{
                    color:
                      (location.pathname.startsWith("/websites") ||
                        location.pathname.startsWith("/offers/import") ||
                        location.pathname.startsWith("/offers/export")) &&
                      "var(--nav-item-title-active) !important",
                  }}
                  data-bs-toggle="collapse"
                  data-bs-target="#navbarVerticalMenuPagesMenu4"
                  aria-expanded={
                    location.pathname.startsWith("/websites") ||
                    location.pathname.startsWith("/offers/import") ||
                    location.pathname.startsWith("/offers/export")
                      ? "true"
                      : "false"
                  }
                  aria-controls="navbarVerticalMenuPagesMenu4"
                >
                  <i className="bi-window-dock nav-icon"></i>
                  <span className="nav-link-title">{t("Websites")}</span>
                </a>
                <div
                  id="navbarVerticalMenuPagesMenu4"
                  className={`nav-collapse collapse ${
                    location.pathname.startsWith("/websites") ||
                    location.pathname.startsWith("/offers/import") ||
                    location.pathname.startsWith("/offers/export")
                      ? "show"
                      : ""
                  }`}
                  data-bs-parent="#navbarVerticalMenuPagesMenu4"
                >
                  <Link
                    className={
                      location.pathname.startsWith("/websites")
                        ? "nav-link active"
                        : "nav-link"
                    }
                    to="/websites"
                  >
                    {t("Your Websites")}
                  </Link>
                  <Link
                    to="/offers/import"
                    className={
                      location.pathname.startsWith("/offers/import")
                        ? "nav-link active"
                        : "nav-link"
                    }
                  >
                    {t("Imports")}
                  </Link>
                  <Link
                    className={
                      location.pathname.startsWith("/offers/export")
                        ? "nav-link active"
                        : "nav-link"
                    }
                    to="/offers/export"
                  >
                    {t("Exports")}
                  </Link>
                </div>
              </div>

              <div className="nav-item">
                <a
                  className={`nav-link dropdown-toggle ${
                    ["/tasks"].includes(location.pathname) ? "active-link" : ""
                  }`}
                  href="#navbarVerticalMenuPagesMenu3"
                  role="button"
                  style={{
                    color:
                      location.pathname === "/tasks" &&
                      "var(--nav-item-title-active) !important",
                  }}
                  data-bs-toggle="collapse"
                  data-bs-target="#navbarVerticalMenuPagesMenu3"
                  aria-expanded={
                    ["/tasks"].includes(location.pathname) ? "true" : "false"
                  }
                  aria-controls="navbarVerticalMenuPagesMenu3"
                >
                  <i className="bi-list-task nav-icon"></i>
                  <span className="nav-link-title">{t("Tasks")}</span>
                </a>

                <div
                  id="navbarVerticalMenuPagesMenu3"
                  className={`nav-collapse collapse ${
                    ["/tasks"].includes(location.pathname) ? "show" : ""
                  }`}
                  data-bs-parent="#navbarVerticalMenuPagesMenu3"
                >
                  <Link className="nav-link" to="/tasks">
                    {t("Tasks")}
                  </Link>
                </div>
              </div>

              {/* Wallet Section */}
              <div className="nav-item">
                <a
                  className={`nav-link dropdown-toggle ${
                    [
                      "/balance",
                      "/contracts",
                      "/orders",
                      "/transactions",
                    ].includes(location.pathname)
                      ? "active-link"
                      : ""
                  }`}
                  style={{
                    color:
                      (location.pathname === "/balance" ||
                      location.pathname === "/contracts" ||
                        location.pathname === "/orders" ||
                        location.pathname === "/transactions") &&
                      "var(--nav-item-title-active) !important",
                  }}
                  href="#navbarVerticalMenuPagesMenu5"
                  role="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#navbarVerticalMenuPagesMenu5"
                  aria-expanded={
                    ["/contracts", "/orders", "/transactions"].includes(
                      location.pathname
                    )
                      ? "true"
                      : "false"
                  }
                  aria-controls="navbarVerticalMenuPagesMenu5"
                >
                  <i className="bi-wallet2 nav-icon"></i>
                  <span className={`nav-link-title`}>{t("Wallet")}</span>
                </a>

                <div
                  id="navbarVerticalMenuPagesMenu5"
                  className={`nav-collapse collapse ${
                    [
                      "/contracts",
                      "/orders",
                      "/transactions",
                      "/balance",
                    ]?.some((path) => location.pathname.startsWith(path))
                      ? "show"
                      : ""
                  }`}
                  data-bs-parent="#navbarVerticalMenuPagesMenu5"
                >
                  <Link className={
                      location.pathname.startsWith("/balance")
                        ? "nav-link active"
                        : "nav-link"
                    } to="/balance">
                    {t("Balance")}
                  </Link>
                  <Link
                    className={
                      location.pathname === "/orders"
                        ? "nav-link active"
                        : "nav-link"
                    }
                    to="/orders"
                  >
                    {t("Orders")}
                  </Link>
                  <Link
                    className={
                      location.pathname.startsWith("/contracts")
                        ? "nav-link active"
                        : "nav-link"
                    }
                    to="/contracts"
                  >
                    {t("Contracts")}
                  </Link>
                  <Link
                    className={
                      location.pathname === "/transactions"
                        ? "nav-link active"
                        : "nav-link"
                    }
                    to="/transactions"
                  >
                    {t("Transactions")}
                  </Link>
                </div>
              </div>
            </div>
            <div className="notification-cart-panel">
              <div
                className="navbar-vertical-footer item-footer"
                style={{
                  right: "unset",
                  paddingLeft: "8px",
                  paddingRight: "20px",
                  border: "unset",
                  width: "100%",
                }}
              >
                <ul
                  className="navbar-vertical-footer-list"
                  style={{ flexDirection: "column" }}
                >
                  <li
                    className="navbar-vertical-footer-list-item btn btn-ghost-secondary btn-icon btn-cart"
                    style={{
                      width: "100%",
                      padding: "0px 16px",
                      borderRadius: "8px",
                    }}
                  >
                    <Link
                      to={"/cart"}
                      className="d-flex justify-content-between"
                      style={{ width: "100%", fontWeight: 400 }}
                    >
                      {cartCount === null ? (
                        <span
                          className="spinner-border spinner-border-sm text-secondary"
                          role="status"
                          aria-hidden="true"
                          style={{ fontSize: "0.75rem" }}
                        ></span>
                      ) : (
                        <>
                          <div>
                            <i className="bi-cart pe-3"></i>
                            <span>{t("Cart")}</span>
                          </div>

                          <div
                            className="btn-status btn-md-status btn-status-secondary text-light btn-cart-items-count"
                            style={{ fontSize: "0.55rem", position: "unset" }}
                          >
                            {cartCount}
                          </div>
                        </>
                      )}
                    </Link>
                  </li>
                  <li
                    className="navbar-vertical-footer-list-item btn btn-ghost-secondary btn-icon"
                    style={{
                      width: "100%",
                      padding: "0px 16px",
                      borderRadius: "8px",
                    }}
                  >
                    <div className="dropdown dropup" style={{ width: "100%" }}>
                      <div
                        type="button"
                        className="d-flex justify-content-between"
                        style={{ width: "100%", fontWeight: 400 }}
                        id="navbarNotificationsDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        data-bs-auto-close="outside"
                        data-bs-dropdown-animation
                      >
                        <div>
                          <i className="bi-bell pe-3"></i>
                          <span>{t("Notifications")}</span>
                        </div>
                        <div>
                          <span
                            className="btn-status btn-sm-status btn-status-danger"
                            style={{ position: "unset" }}
                          ></span>
                        </div>
                      </div>

                      <div
                        className="dropdown-menu navbar-dropdown-menu navbar-dropdown-menu-borderless dropdown-small-notification"
                        aria-labelledby="navbarNotificationsDropdown"
                        style={{
                          background: "unset",
                        }}
                      >
                        <div className="card" style={{ height: "400px" }}>
                          <div className="card-header card-header-content-between">
                            <h4 className="card-title mb-0">
                              {t("Notifications")}
                            </h4>

                            <div className="dropdown">
                              <button
                                type="button"
                                className="btn btn-icon btn-sm btn-ghost-secondary rounded-circle"
                                id="navbarNotificationsDropdownSettings"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                              >
                                <i className="bi-three-dots-vertical"></i>
                              </button>

                              <div
                                className="dropdown-menu dropdown-menu-end navbar-dropdown-menu navbar-dropdown-menu-borderless"
                                aria-labelledby="navbarNotificationsDropdownSettings"
                              >
                                <span className="dropdown-header">
                                  {t("Settings")}
                                </span>
                                <a
                                  className="dropdown-item"
                                  href="/offers-search"
                                >
                                  <i className="bi-archive dropdown-item-icon"></i>{" "}
                                  {t("Archive all")}
                                </a>
                                <a
                                  className="dropdown-item"
                                  href="/offers-search"
                                >
                                  <i className="bi-check2-all dropdown-item-icon"></i>{" "}
                                  {t("Mark all as read")}
                                </a>
                                <a
                                  className="dropdown-item"
                                  href="/offers-search"
                                >
                                  <i className="bi-toggle-off dropdown-item-icon"></i>{" "}
                                  {t("Disable notifications")}
                                </a>
                                <a
                                  className="dropdown-item"
                                  href="/offers-search"
                                >
                                  <i className="bi-gift dropdown-item-icon"></i>{" "}
                                  {t("What's new?")}
                                </a>
                                <div className="dropdown-divider"></div>
                                <span className="dropdown-header">
                                  {t("Feedback")}
                                </span>
                                <a
                                  className="dropdown-item"
                                  href="/offers-search"
                                >
                                  <i className="bi-chat-left-dots dropdown-item-icon"></i>{" "}
                                  {t("Report")}
                                </a>
                              </div>
                            </div>
                          </div>

                          <ul
                            className="nav nav-tabs nav-justified"
                            id="notificationTab"
                            role="tablist"
                          >
                            <li className="nav-item ">
                              <a
                                className="nav-link active notification-item"
                                href="#notificationNavOne"
                                id="notificationNavOne-tab"
                                data-bs-toggle="tab"
                                data-bs-target="#notificationNavOne"
                                role="tab"
                                aria-controls="notificationNavOne"
                                aria-selected="true"
                              >
                                {t("Messages")} (3)
                              </a>
                            </li>
                            <li className="nav-item ">
                              <a
                                className="nav-link notification-item"
                                href="#notificationNavTwo"
                                id="notificationNavTwo-tab"
                                data-bs-toggle="tab"
                                data-bs-target="#notificationNavTwo"
                                role="tab"
                                aria-controls="notificationNavTwo"
                                aria-selected="false"
                              >
                                {t("Archived")}
                              </a>
                            </li>
                          </ul>

                          <div className="card-body-height">
                            <div
                              className="tab-content"
                              id="notificationTabContent"
                            >
                              <div
                                className="tab-pane fade show active"
                                id="notificationNavOne"
                                role="tabpanel"
                                aria-labelledby="notificationNavOne-tab"
                              >
                                <ul className="list-group list-group-flush navbar-card-list-group">
                                  <li className="list-group-item form-check-select">
                                    <div className="row">
                                      <div className="col-auto">
                                        <div className="d-flex align-items-center">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              value=""
                                              id="notificationCheck1"
                                            />
                                            <label
                                              className="form-check-label"
                                              htmlFor="notificationCheck1"
                                            ></label>
                                            <span className="form-check-stretched-bg"></span>
                                          </div>
                                          <img
                                            className="avatar avatar-sm avatar-circle"
                                            src="https://static.articlesfactory.ai/app/img/160x160/img3.jpg"
                                            alt="Description"
                                          />
                                        </div>
                                      </div>
                                      <div className="col ms-n2">
                                        <h5 className="mb-1">Brian Warner</h5>
                                        <p className="text-body fs-5">
                                          {t("changed an issue from")} "In
                                          Progress" {t("to")}{" "}
                                          <span className="badge bg-success">
                                            {t("Review")}
                                          </span>
                                        </p>
                                      </div>
                                      <small className="col-auto text-muted text-cap">
                                        2hr
                                      </small>
                                    </div>

                                    {/* eslint-disable-next-line */}
                                    <a
                                      className="stretched-link"
                                      href="/offers-search"
                                    ></a>
                                  </li>

                                  <li className="list-group-item form-check-select">
                                    <div className="row">
                                      <div className="col-auto">
                                        <div className="d-flex align-items-center">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              value=""
                                              id="notificationCheck2"
                                              checked
                                              onChange={() => {}}
                                            />
                                            <label
                                              className="form-check-label"
                                              htmlFor="notificationCheck2"
                                            ></label>
                                            <span className="form-check-stretched-bg"></span>
                                          </div>
                                          <div className="avatar avatar-sm avatar-soft-dark avatar-circle">
                                            <span className="avatar-initials">
                                              K
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="col ms-n2">
                                        <h5 className="mb-1">Klara Hampton</h5>
                                        <p className="text-body fs-5">
                                          {t("mentioned you in a comment")}
                                        </p>
                                        <blockquote className="blockquote blockquote-sm">
                                          {t(
                                            "Nice work, love! You really nailed it. Keep it up!"
                                          )}
                                        </blockquote>
                                      </div>

                                      <small className="col-auto text-muted text-cap">
                                        10hr
                                      </small>
                                    </div>

                                    {/* eslint-disable-next-line */}
                                    <a
                                      className="stretched-link"
                                      href="/offers-search"
                                    ></a>
                                  </li>

                                  <li className="list-group-item form-check-select">
                                    <div className="row">
                                      <div className="col-auto">
                                        <div className="d-flex align-items-center">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              value=""
                                              id="notificationCheck3"
                                            />
                                            <label
                                              className="form-check-label"
                                              htmlFor="notificationCheck3"
                                            ></label>
                                            <span className="form-check-stretched-bg"></span>
                                          </div>
                                          <div className="avatar avatar-sm avatar-circle">
                                            <img
                                              className="avatar-img"
                                              src="https://static.articlesfactory.ai/app/img/160x160/img10.jpg"
                                              alt="Description"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      <div className="col ms-n2">
                                        <h5 className="mb-1">Ruby Walter</h5>
                                        <p className="text-body fs-5">
                                          {t("joined the Slack group")} HS Team
                                        </p>
                                      </div>

                                      <small className="col-auto text-muted text-cap">
                                        3dy
                                      </small>
                                    </div>

                                    {/* eslint-disable-next-line */}
                                    <a
                                      className="stretched-link"
                                      href="/offers-search"
                                    ></a>
                                  </li>

                                  <li className="list-group-item form-check-select">
                                    <div className="row">
                                      <div className="col-auto">
                                        <div className="d-flex align-items-center">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              value=""
                                              id="notificationCheck4"
                                            />
                                            <label
                                              className="form-check-label"
                                              htmlFor="notificationCheck4"
                                            ></label>
                                            <span className="form-check-stretched-bg"></span>
                                          </div>
                                          <div className="avatar avatar-sm avatar-circle">
                                            <img
                                              className="avatar-img"
                                              src="https://static.articlesfactory.ai/app/svg/brands/google-icon.svg"
                                              alt="Description"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      <div className="col ms-n2">
                                        <h5 className="mb-1">
                                          {t("from")} Google
                                        </h5>
                                        <p className="text-body fs-5">
                                          {t(
                                            "Start using forms to capture the information of prospects visiting your Google website"
                                          )}
                                        </p>
                                      </div>

                                      <small className="col-auto text-muted text-cap">
                                        17dy
                                      </small>
                                    </div>

                                    {/* eslint-disable-next-line */}
                                    <a
                                      className="stretched-link"
                                      href="/offers-search"
                                    ></a>
                                  </li>

                                  <li className="list-group-item form-check-select">
                                    <div className="row">
                                      <div className="col-auto">
                                        <div className="d-flex align-items-center">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              value=""
                                              id="notificationCheck5"
                                            />
                                            <label
                                              className="form-check-label"
                                              htmlFor="notificationCheck5"
                                            ></label>
                                            <span className="form-check-stretched-bg"></span>
                                          </div>
                                          <div className="avatar avatar-sm avatar-circle">
                                            <img
                                              className="avatar-img"
                                              src="https://static.articlesfactory.ai/app/img/160x160/img7.jpg"
                                              alt="Description"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      <div className="col ms-n2">
                                        <h5 className="mb-1">Sara Villar</h5>
                                        <p className="text-body fs-5">
                                          {t("completed")}{" "}
                                          <i className="bi-journal-bookmark-fill text-primary"></i>{" "}
                                          FD-7 task
                                        </p>
                                      </div>

                                      <small className="col-auto text-muted text-cap">
                                        2mn
                                      </small>
                                    </div>

                                    {/* eslint-disable-next-line */}
                                    <a
                                      className="stretched-link"
                                      href="/offers-search"
                                    ></a>
                                  </li>
                                </ul>
                              </div>

                              <div
                                className="tab-pane fade"
                                id="notificationNavTwo"
                                role="tabpanel"
                                aria-labelledby="notificationNavTwo-tab"
                              >
                                <ul className="list-group list-group-flush navbar-card-list-group">
                                  <li className="list-group-item form-check-select">
                                    <div className="row">
                                      <div className="col-auto">
                                        <div className="d-flex align-items-center">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              value=""
                                              id="notificationCheck6"
                                            />
                                            <label
                                              className="form-check-label"
                                              htmlFor="notificationCheck6"
                                            ></label>
                                            <span className="form-check-stretched-bg"></span>
                                          </div>
                                          <div className="avatar avatar-sm avatar-soft-dark avatar-circle">
                                            <span className="avatar-initials">
                                              A
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="col ms-n2">
                                        <h5 className="mb-1">Anne Richard</h5>
                                        <p className="text-body fs-5">
                                          {t(
                                            "accepted your invitation to join Notion"
                                          )}
                                        </p>
                                      </div>

                                      <small className="col-auto text-muted text-cap">
                                        1dy
                                      </small>
                                    </div>

                                    {/* eslint-disable-next-line */}
                                    <a
                                      className="stretched-link"
                                      href="/offers-search"
                                    ></a>
                                  </li>

                                  <li className="list-group-item form-check-select">
                                    <div className="row">
                                      <div className="col-auto">
                                        <div className="d-flex align-items-center">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              value=""
                                              id="notificationCheck7"
                                            />
                                            <label
                                              className="form-check-label"
                                              htmlFor="notificationCheck7"
                                            ></label>
                                            <span className="form-check-stretched-bg"></span>
                                          </div>
                                          <div className="avatar avatar-sm avatar-circle">
                                            <img
                                              className="avatar-img"
                                              src="https://static.articlesfactory.ai/app/img/160x160/img5.jpg"
                                              alt="Description"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      <div className="col ms-n2">
                                        <h5 className="mb-1">Finch Hoot</h5>
                                        <p className="text-body fs-5">
                                          left Slack group HS projects
                                        </p>
                                      </div>

                                      <small className="col-auto text-muted text-cap">
                                        1dy
                                      </small>
                                    </div>

                                    {/* eslint-disable-next-line */}
                                    <a
                                      className="stretched-link"
                                      href="/offers-search"
                                    ></a>
                                  </li>

                                  <li className="list-group-item form-check-select">
                                    <div className="row">
                                      <div className="col-auto">
                                        <div className="d-flex align-items-center">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              value=""
                                              id="notificationCheck8"
                                            />
                                            <label
                                              className="form-check-label"
                                              htmlFor="notificationCheck8"
                                            ></label>
                                            <span className="form-check-stretched-bg"></span>
                                          </div>
                                          <div className="avatar avatar-sm avatar-secondary avatar-circle">
                                            <span className="avatar-initials">
                                              HS
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="col ms-n2">
                                        <h5 className="mb-1">Htmlstream</h5>
                                        <p className="text-body fs-5">
                                          {t("you earned a 'Top endorsed'")}{" "}
                                          <i className="bi-patch-check-fill text-primary"></i>{" "}
                                          badge
                                        </p>
                                      </div>

                                      <small className="col-auto text-muted text-cap">
                                        6dy
                                      </small>
                                    </div>

                                    {/* eslint-disable-next-line */}
                                    <a
                                      className="stretched-link"
                                      href="/offers-search"
                                    ></a>
                                  </li>

                                  <li className="list-group-item form-check-select">
                                    <div className="row">
                                      <div className="col-auto">
                                        <div className="d-flex align-items-center">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              value=""
                                              id="notificationCheck9"
                                            />
                                            <label
                                              className="form-check-label"
                                              htmlFor="notificationCheck9"
                                            ></label>
                                            <span className="form-check-stretched-bg"></span>
                                          </div>
                                          <div className="avatar avatar-sm avatar-circle">
                                            <img
                                              className="avatar-img"
                                              src="https://static.articlesfactory.ai/app/img/160x160/img8.jpg"
                                              alt="Description"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      <div className="col ms-n2">
                                        <h5 className="mb-1">Linda Bates</h5>
                                        <p className="text-body fs-5">
                                          {t("Accepted your connection")}
                                        </p>
                                      </div>

                                      <small className="col-auto text-muted text-cap">
                                        17dy
                                      </small>
                                    </div>

                                    {/* eslint-disable-next-line */}
                                    <a
                                      className="stretched-link"
                                      href="/offers-search"
                                    ></a>
                                  </li>

                                  <li className="list-group-item form-check-select">
                                    <div className="row">
                                      <div className="col-auto">
                                        <div className="d-flex align-items-center">
                                          <div className="form-check">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              value=""
                                              id="notificationCheck10"
                                            />
                                            <label
                                              className="form-check-label"
                                              htmlFor="notificationCheck10"
                                            ></label>
                                            <span className="form-check-stretched-bg"></span>
                                          </div>
                                          <div className="avatar avatar-sm avatar-soft-dark avatar-circle">
                                            <span className="avatar-initials">
                                              L
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="col ms-n2">
                                        <h5 className="mb-1">Lewis Clarke</h5>
                                        <p className="text-body fs-5">
                                          {t("completed")}{" "}
                                          <i className="bi-journal-bookmark-fill text-primary"></i>{" "}
                                          FD-134 {t("task")}
                                        </p>
                                      </div>

                                      <small className="col-auto text-muted text-cap">
                                        2mts
                                      </small>
                                    </div>

                                    {/* eslint-disable-next-line */}
                                    <a
                                      className="stretched-link"
                                      href="/offers-search"
                                    ></a>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          <a
                            className="card-footer text-center"
                            href="/offers-search"
                          >
                            {t("View all notifications")}{" "}
                            <i className="bi-chevron-right"></i>
                          </a>
                        </div>
                      </div>
                    </div>
                  </li>

                  <li
                    className="navbar-vertical-footer-list-item btn btn-icon btn-ghost-secondary"
                    style={{
                      width: "100%",
                      padding: "0px 16px",
                      borderRadius: "8px",
                    }}
                  >
                    <div className="dropdown dropup" style={{ width: "100%" }}>
                      <div
                        type="button"
                        className="d-flex justify-content-start gap-3"
                        style={{ width: "80%", fontWeight: 400 }}
                        id="navbarAppsDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        data-bs-dropdown-animation
                      >
                        <i className="bi-app-indicator pr-2"></i> {t("Lorem")}
                      </div>

                      <div
                        className="dropdown-menu navbar-dropdown-menu navbar-dropdown-menu-borderless dropdown-small-notification"
                        aria-labelledby="navbarAppsDropdown"
                        style={{
                          background: "unset",
                        }}
                      >
                        <div className="card">
                          <div className="card-header">
                            <h4 className="card-title">
                              {t("Web apps & services")}
                            </h4>
                          </div>

                          <div className="card-body card-body-height">
                            <a className="dropdown-item" href="/offers-search">
                              <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                  <img
                                    className="avatar avatar-xs avatar-4x3"
                                    src="https://static.articlesfactory.ai/app/svg/brands/atlassian-icon.svg"
                                    alt="Description"
                                  />
                                </div>
                                <div className="flex-grow-1 text-truncate ms-3">
                                  <h5 className="mb-0">Atlassian</h5>
                                  <p className="card-text text-body">
                                    {t("Security and control across Cloud")}
                                  </p>
                                </div>
                              </div>
                            </a>

                            <a className="dropdown-item" href="/offers-search">
                              <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                  <img
                                    className="avatar avatar-xs avatar-4x3"
                                    src="https://static.articlesfactory.ai/app/svg/brands/slack-icon.svg"
                                    alt="Description"
                                  />
                                </div>
                                <div className="flex-grow-1 text-truncate ms-3">
                                  <h5 className="mb-0">
                                    Slack{" "}
                                    <span className="badge bg-primary rounded-pill text-uppercase ms-1">
                                      Try
                                    </span>
                                  </h5>
                                  <p className="card-text text-body">
                                    {t("Email collaboration software")}
                                  </p>
                                </div>
                              </div>
                            </a>

                            <a className="dropdown-item" href="/offers-search">
                              <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                  <img
                                    className="avatar avatar-xs avatar-4x3"
                                    src="https://static.articlesfactory.ai/app/svg/brands/google-webdev-icon.svg"
                                    alt="Description"
                                  />
                                </div>
                                <div className="flex-grow-1 text-truncate ms-3">
                                  <h5 className="mb-0">Google webdev</h5>
                                  <p className="card-text text-body">
                                    {t("Work involved in developing a website")}
                                  </p>
                                </div>
                              </div>
                            </a>

                            <a className="dropdown-item" href="/offers-search">
                              <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                  <img
                                    className="avatar avatar-xs avatar-4x3"
                                    src="https://static.articlesfactory.ai/app/svg/brands/frontapp-icon.svg"
                                    alt="Description"
                                  />
                                </div>
                                <div className="flex-grow-1 text-truncate ms-3">
                                  <h5 className="mb-0">{t("Frontapp")}</h5>
                                  <p className="card-text text-body">
                                    {t("The inbox for teams")}
                                  </p>
                                </div>
                              </div>
                            </a>

                            <a className="dropdown-item" href="/offers-search">
                              <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                  <img
                                    className="avatar avatar-xs avatar-4x3"
                                    src="https://static.articlesfactory.ai/app/svg/illustrations/review-rating-shield.svg"
                                    alt="Description"
                                  />
                                </div>
                                <div className="flex-grow-1 text-truncate ms-3">
                                  <h5 className="mb-0">HS {t("Support")}</h5>
                                  <p className="card-text text-body">
                                    {t("Customer service and support")}
                                  </p>
                                </div>
                              </div>
                            </a>

                            <a className="dropdown-item" href="/offers-search">
                              <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                  <div className="avatar avatar-sm avatar-soft-dark">
                                    <span className="avatar-initials">
                                      <i className="bi-grid"></i>
                                    </span>
                                  </div>
                                </div>
                                <div className="flex-grow-1 text-truncate ms-3">
                                  <h5 className="mb-0">
                                    {t("More Front products")}
                                  </h5>
                                  <p className="card-text text-body">
                                    {t("Check out more")} HS products
                                  </p>
                                </div>
                              </div>
                            </a>
                          </div>

                          <a
                            className="card-footer text-center"
                            href="/offers-search"
                          >
                            {t("View all apps")}{" "}
                            <i className="bi-chevron-right"></i>
                          </a>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <div className="navbar-vertical-footer">
              <ul className="navbar-vertical-footer-list">
                <li className="navbar-vertical-footer-list-item">
                  <div className="dropdown dropup">
                    <button
                      type="button"
                      className="btn btn-ghost-secondary btn-icon rounded-circle"
                      id="selectThemeDropdown"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      data-bs-dropdown-animation
                    >
                      <i className={`bi ${selectedIcon}`}></i>
                    </button>

                    <div
                      className="dropdown-menu navbar-dropdown-menu navbar-dropdown-menu-borderless"
                      aria-labelledby="selectThemeDropdown"
                    >
                      {/* eslint-disable-next-line */}
                      <button
                        className="dropdown-item"
                        data-icon="bi-moon-stars"
                        data-value="auto"
                      >
                        <i className="bi-moon-stars me-2"></i>
                        <span
                          className="text-truncate"
                          title="Auto (system default)"
                        >
                          {t("Auto (system default)")}
                        </span>
                      </button>
                      {/* eslint-disable-next-line */}
                      <button
                        className="dropdown-item"
                        data-icon="bi-brightness-high"
                        data-value="default"
                      >
                        <i className="bi-brightness-high me-2"></i>
                        <span
                          className="text-truncate"
                          title="Default (light mode)"
                        >
                          {t("Default (light mode)")}
                        </span>
                      </button>
                      {/* eslint-disable-next-line */}
                      <button
                        className="dropdown-item active"
                        data-icon="bi-moon"
                        data-value="dark"
                      >
                        <i className="bi-moon me-2"></i>
                        <span className="text-truncate" title="Dark">
                          {t("Dark")}
                        </span>
                      </button>
                    </div>
                  </div>
                </li>

                <li className="navbar-vertical-footer-list-item">
                  <div className="dropdown dropup">
                    <button
                      type="button"
                      className="btn btn-ghost-secondary btn-icon rounded-circle"
                      id="otherLinksDropdown"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      data-bs-dropdown-animation
                    >
                      <i className="bi-info-circle"></i>
                    </button>

                    <div
                      className="dropdown-menu navbar-dropdown-menu-borderless"
                      aria-labelledby="otherLinksDropdown"
                    >
                      <span className="dropdown-header">{t("Help")}</span>
                      <a className="dropdown-item" href="/offers-search">
                        <i className="bi-journals dropdown-item-icon"></i>
                        <span
                          className="text-truncate"
                          title="Resources &amp; tutorials"
                        >
                          {t("Resources & tutorials")}
                        </span>
                      </a>
                      <a className="dropdown-item" href="/offers-search">
                        <i className="bi-command dropdown-item-icon"></i>
                        <span
                          className="text-truncate"
                          title="Keyboard shortcuts"
                        >
                          {t("Keyboard shortcuts")}
                        </span>
                      </a>
                      <a className="dropdown-item" href="/offers-search">
                        <i className="bi-alt dropdown-item-icon"></i>
                        <span
                          className="text-truncate"
                          title="Connect other apps"
                        >
                          {t("Connect other apps")}
                        </span>
                      </a>
                      <a className="dropdown-item" href="/offers-search">
                        <i className="bi-gift dropdown-item-icon"></i>
                        <span className="text-truncate" title="What's new?">
                          {t("What's new?")}
                        </span>
                      </a>
                      <div className="dropdown-divider"></div>
                      <span className="dropdown-header">{t("Contacts")}</span>
                      <a className="dropdown-item" href="/offers-search">
                        <i className="bi-chat-left-dots dropdown-item-icon"></i>
                        <span className="text-truncate" title="Contact support">
                          {t("Contact support")}
                        </span>
                      </a>
                    </div>
                  </div>
                </li>

                <li className="navbar-vertical-footer-list-item">
                  <div className="dropdown dropup">
                    <button
                      type="button"
                      className="btn btn-ghost-secondary btn-icon rounded-circle"
                      id="selectLanguageDropdown"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                      data-bs-dropdown-animation
                    >
                      <img
                        className="avatar avatar-xss avatar-circle"
                        src={selectedLanguage.flag}
                        alt={`${selectedLanguage.name} Flag`}
                      />
                    </button>

                    <div
                      className="dropdown-menu navbar-dropdown-menu-borderless"
                      aria-labelledby="selectLanguageDropdown"
                    >
                      <span className="dropdown-header">
                        {t("Select language")}
                      </span>

                      {Object.keys(languageOptions).map((lng) => (
                        <button
                          key={lng}
                          className={
                            language === lng
                              ? "dropdown-item active"
                              : "dropdown-item"
                          }
                          onClick={() => changeLanguage(lng)}
                        >
                          <img
                            className="avatar avatar-xss avatar-circle me-2"
                            src={languageOptions[lng].flag}
                            alt={`${languageOptions[lng].name} Flag`}
                          />
                          <span
                            className="text-truncate"
                            title={languageOptions[lng].name}
                          >
                            {languageOptions[lng].name}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
